import { CheckCircle2, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { StaticRow, type RowTone } from './ui/ListRows';

/** Stripe colour of a deliverable: green published, blue delivered/approved, amber in production, red rejected. */
export function deliverableTone(status: string | undefined): RowTone {
  switch (status) {
    case 'Published': return 'success';
    case 'Approved':
    case 'Delivered':
    case 'Submitted': return 'info';
    case 'Production':
    case 'In Production': return 'warning';
    case 'Rejected': return 'danger';
    default: return 'neutral';
  }
}

interface DeliverableRowProps {
  index: number;
  count: number;
  content: string;
  date: string;
  status: string;
  statusOptions: { value: string; label: string }[];
  contentPlaceholder: string;
  onContent: (value: string) => void;
  onDate: (value: string) => void;
  onStatus: (value: string) => void;
  onMove: (direction: 'up' | 'down') => void;
  /** Omit to hide the delete button (e.g. the last deliverable). */
  onRemove?: () => void;
  /** Creators' deliverables also carry their fee and commission. */
  money?: {
    fee: number;
    commission: number;
    onFee: (value: number) => void;
    onCommission: (value: number) => void;
  };
  labels: { moveUp: string; moveDown: string; remove: string; published: string; fee: string; commission: string };
}

/** One deliverable on one line (wrapping on a phone), edited in place. */
export function DeliverableRow({
  index,
  count,
  content,
  date,
  status,
  statusOptions,
  contentPlaceholder,
  onContent,
  onDate,
  onStatus,
  onMove,
  onRemove,
  money,
  labels,
}: DeliverableRowProps) {
  return (
    <StaticRow tone={deliverableTone(status)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-6 h-6 grid place-items-center text-xs font-semibold text-gray-500 bg-gray-100 rounded-full shrink-0">
          {index + 1}
        </span>
        <input
          type="text"
          placeholder={contentPlaceholder}
          value={content}
          onChange={(e) => onContent(e.target.value)}
          className="cm-input !h-9 flex-1 min-w-[12rem]"
        />
        {money && (
          <>
            <label className="cm-affix has-left w-32 shrink-0" title={labels.fee}>
              <span>€</span>
              <input
                type="number"
                value={money.fee || ''}
                onChange={(e) => money.onFee(parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                placeholder={labels.fee}
                aria-label={labels.fee}
                className="cm-input cm-numeric !h-9"
              />
            </label>
            <label className="cm-affix has-left w-36 shrink-0" title={labels.commission}>
              <span className="!text-[10px] font-semibold">COM</span>
              <input
                type="number"
                value={money.commission || ''}
                onChange={(e) => money.onCommission(parseFloat(e.target.value) || 0)}
                step="0.01"
                min="0"
                placeholder={labels.commission}
                aria-label={labels.commission}
                className="cm-input cm-numeric !h-9 !pl-12"
              />
            </label>
          </>
        )}
        {/* Date, status and actions wrap together onto a second line when needed */}
        <span className="ml-auto flex items-center justify-end gap-2">
        <input
          type="date"
          value={date}
          onChange={(e) => onDate(e.target.value)}
          className="cm-date !h-9 !w-[150px]"
        />
        <select
          value={status}
          onChange={(e) => onStatus(e.target.value)}
          className="cm-select !h-9 !w-[140px]"
        >
          {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="flex items-center gap-0.5">
          {/* Same width on every row so the columns line up */}
          <span className="w-5 grid place-items-center">
            {status === 'Published' && (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-label={labels.published} />
            )}
          </span>
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={index === 0}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={labels.moveUp}
            title={labels.moveUp}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={index === count - 1}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={labels.moveDown}
            title={labels.moveDown}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
              aria-label={labels.remove}
              title={labels.remove}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </span>
        </span>
      </div>
    </StaticRow>
  );
}
